import { FC, useEffect } from 'react'
import Button from 'baby-design/components/Button/Button'
import Link from 'components/link'
import { track, useTracking } from 'lib/analytics'
import Slide, { Title } from '../../Slide/Slide'
import { useNavigate } from 'react-router-dom-v5-compat'
import css from './AddChromeExtension.scss'
import LightBulb from './LightBulb.svg'
import BabylistHeart from './BabylistHeart.svg'
import { BABYLIST_EXTENSION_URL } from 'shared/constants'

interface AddChromeExtensionSlideProps {
  nextPath: string
  isOnboarding: boolean
}

const AddChromeExtensionSlide: FC<AddChromeExtensionSlideProps> = ({
  nextPath,
  isOnboarding,
}) => {
  const tracker = useTracking()

  const navigate = useNavigate()

  const emitBabylistButtonTrackingEvent = () => {
    tracker.trackEvent({
      event: track.onboardingQuestionAnswered,
      eventLocation: track.EventLocation.ONBOARDING_MINUS_BABYLIST_BUTTON,
    })
  }

  const handleOnClick = () => {
    emitBabylistButtonTrackingEvent()
    if (isOnboarding) {
      window.location.href = nextPath
    } else {
      navigate(nextPath)
    }
  }

  const handleExtensionInstall = () => {
    emitBabylistButtonTrackingEvent()
    document.cookie = `browser_extension_referer=${window.location.origin}/registry/new/try-it-out;path=/`
    window.open(BABYLIST_EXTENSION_URL)
  }

  useEffect(() => {
    // add the class css.AddChromeExtension--body to the body
    // to add a background color to the body
    document.body.classList.add(css.AddChromeExtension__body)
    return () => {
      document.body.classList.remove(css.AddChromeExtension__body)
    }
  }, [css])

  return (
    <Slide className={css.AddChromeExtension}>
      <Title className="mbl">
        Real quick!
        <br />
        Get set up to add items from any website
      </Title>
      <p className={css.AddChromeExtension__subtitle}>
        Easily add items to your registry after adding the Babylist Button to
        your browser.
      </p>
      <Button
        size="sm"
        onClick={handleExtensionInstall}
        className={css.AddChromeExtension__call_to_action}
        variant="primary"
      >
        Install to start adding
      </Button>
      <div className={css.AddChromeExtension__grid}>
        <div className={css.AddChromeExtension__grid__item}>
          <img
            src="https://images.babylist.com/image/upload/v1710424326/step_1_add_babylist_button_jvwa7o.png"
            width="200"
            height="140"
          />
          <h3>Step 1</h3>
          <p>Add the Babylist Button to your browser</p>
        </div>
        <div className={css.AddChromeExtension__grid__item}>
          <img
            src="https://images.babylist.com/image/upload/v1710424736/step_2_visit_any_website_h2v0nc.png"
            width="200"
            height="140"
          />
          <h3>Step 2</h3>
          <p>Visit any store website and find your items.</p>
        </div>
        <div className={css.AddChromeExtension__grid__item}>
          <img
            src="https://images.babylist.com/image/upload/v1710424327/step_3_click_wojfve.png"
            width="200"
            height="140"
          />
          <h3>Step 3</h3>
          <p>
            Click the{' '}
            <BabylistHeart className={css['AddChromeExtension__inline-icon']} />{' '}
            in your toolbar to add directly from any store.
          </p>
        </div>
      </div>
      <div className={css.AddChromeExtension__callout}>
        <LightBulb />
        <p>
          You'll need our Babylist Button to be able to add items from other
          websites.&nbsp;
          <Link onClick={handleExtensionInstall}>Get it now</Link>
        </p>
      </div>
      <Link className={css.AddChromeExtension__link} onClick={handleOnClick}>
        I'll do this later
      </Link>
    </Slide>
  )
}

export default AddChromeExtensionSlide
